import Input from '../Form/Input';
import React, { forwardRef, useEffect, useState } from 'react';
import styled from "styled-components";
import classnames from "classnames";
import IBuildrProps from 'src/utilities/IBuildrProps';
import { useIsMobile } from '../../context/MobileContext';
import { AutomationPlacement, AutomationTestIdElementType, AutomationTestIdFeature, createDataTestId } from '../../utilities/automation';
import useLanguage from "../../utilities/useLanguage";
import { getTranslation } from './translations';

interface ILoginProps extends IBuildrProps {
    isModal?: boolean;
}

const Login = forwardRef(({ cmsId, editorClassObj, className, isModal }: ILoginProps, ref: any) => {
    const [outageMessage, setOutageMessage] = useState(null);
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isDisabled, setIsDisabled] = useState(true);
    const { isMobileWidth } = useIsMobile();
    const { lang } = useLanguage();
    const baseAccountUrl = `https://o.macu.com${isMobileWidth ? '/mobile' : ''}`;
    const placement = isModal ? AutomationPlacement.MODAL : AutomationPlacement.HOME_PAGE;

    function handleInput(e) {
        setUsername(e.target.value);
    }

    function handlePasswordInput(e) {
        setPassword(e.target.value);
    }
    useEffect(() => {
        // console.log('Start getOutageMessages useEffect');

        async function getOutageMessages() {
            try {
                // console.log('Making getOutageMessages api call');
                // Add the timestamp to bypass the API cache so the outage message will immediately appear if marketing changes it.
                const timestamp = Math.floor(Date.now() / 10000);
                const response = await fetch(`${process.env.GATSBY_API_URL}/table/settings/outage_messages?timestamp=${timestamp}`);
                // console.log('Made getOutageMessages api call');
                const { value } = await response.json();
                const messageArray = JSON.parse(value);
                const activeMessage = messageArray.find(message => message.active);
                setOutageMessage(activeMessage);
            } catch (error) {
                console.warn('could not retrieve outage messages', error)
            }
        }

        getOutageMessages();
    }, []);

    useEffect(() => {
        if (password.length > 0 && username.length > 0) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }
    }, [password, username])

    if (outageMessage?.[lang]) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center h-100 outage-message">
                <strong>{outageMessage[lang].title}</strong>
                <p className="text-center px-3">{outageMessage[lang].message}</p>
            </div>
        );
    }

    return (
        <div data-id={cmsId} className={classnames(editorClassObj, className)} ref={ref}>
            <form method="post" data-test-id={createDataTestId({placement, feature: AutomationTestIdFeature.LOGIN, element: AutomationTestIdElementType.FORM, specifier: 'loginAction'})} action={`${baseAccountUrl}/Authentication/Username`}>
                <Input className={"form-group"}
                    hasValue={username.length > 0}
                    id={"username"}
                    label={getTranslation(lang).username}>
                    <input
                        id={!isModal ? 'hpLoginUsernameInput' : null}
                        type="text"
                        name="username"
                        value={username}
                        onChange={handleInput}
                        className={classnames("form-control", "px-3", "has-label", "border-0")}
                        data-test-id={createDataTestId({placement, feature: AutomationTestIdFeature.LOGIN, element: AutomationTestIdElementType.INPUT, specifier: 'username'})}
                        autoComplete="off" />
                </Input>
                <Input className={"form-group"}
                    hasValue={password.length > 0}
                    id={"password"}
                    label={getTranslation(lang).password}>
                    <input
                        id={!isModal ? 'hpLoginPasswordInput' : null}
                        type="password"
                        name="password"
                        value={password}
                        onChange={handlePasswordInput}
                        className={classnames("form-control", "px-3", "has-label", "border-0")}
                        data-test-id={createDataTestId({placement, feature: AutomationTestIdFeature.LOGIN, element: AutomationTestIdElementType.INPUT, specifier: 'password'})}
                        autoComplete="off" />
                </Input>
                <div className={classnames("d-flex login-footer", {'flex-column' : isModal})}>
                    <button id={!isModal ? 'hpLoginButton' : null} type="submit" disabled={isDisabled} className={classnames("btn btn-light login-btn", {'w-100' : isModal})} data-test-id={createDataTestId({placement, feature: AutomationTestIdFeature.LOGIN, element: AutomationTestIdElementType.BUTTON, specifier: 'loginSubmit'})}>{getTranslation(lang).login}</button>
                    <div className={classnames("d-flex login-links", {'isModal' : isModal})}>
                        <span>
                            {getTranslation(lang).forgot} <a data-test-id={createDataTestId({placement, feature: AutomationTestIdFeature.LOGIN, element: AutomationTestIdElementType.LINK, specifier: 'forgotId'})} href={`${baseAccountUrl}/ForgotUsername`}>{getTranslation(lang).username}</a> {getTranslation(lang).or}  <a data-test-id={createDataTestId({placement, feature: AutomationTestIdFeature.LOGIN, element: AutomationTestIdElementType.LINK, specifier: 'forgotPassword'})} href={`${baseAccountUrl}/ForgotPassword`}>{getTranslation(lang).password}</a>
                        </span>
                        <span>
                            <a className="register-link" href={`${baseAccountUrl}/Registration`} data-test-id={createDataTestId({placement, feature: AutomationTestIdFeature.LOGIN, element: AutomationTestIdElementType.LINK, specifier: 'register'})}>
                                { getTranslation(lang).register }
                            </a>
                        </span>
                    </div>
                </div>
            </form>
        </div>
    )
});

export default styled(Login)`
    .login-btn {
        padding: .7rem .3rem;
        min-width: 100px;
        margin: auto;
        @media (max-width: ${props => props.theme.gridBreakpoints?.md}) {
            width: 100%;
        }
    }
    .login-footer {
        flex-direction: row;
        @media (max-width: ${props => props.theme.gridBreakpoints?.md}) {
            flex-direction: column;
        }
    }
    .login-links {
        flex-grow: 1;
        flex-direction: column;
        margin-left: .75rem;

        @media (max-width: ${props => props.theme.gridBreakpoints?.md}) {
            margin-top: .5rem;
            margin-left: 0;
        }

        a {
            color: ${props => props.theme.gray750};
        }
        span, a {
            font-size: 0.889rem;
            line-height: 1.5rem;
        }
        color: rgb(88, 89, 93);
        * {
            color: rgb(88, 89, 93);
        }

        .register-link {
            margin-right: 0.778rem;;
        }

        &.isModal {
            margin-top: 2.056rem;
            margin-left: 0;
            flex-direction: column-reverse;

            .register-link {
                margin-bottom: 0.778rem;
            }

            a {
                color: #000000;
            }

            @media (max-width: ${props => props.theme.gridBreakpoints?.md}) {
                flex-direction: row-reverse;
                justify-content: start;
                .register-link {
                    margin-bottom: 0;
                }

            }
        }
    }

    .form-group label {
        text-transform: capitalize;
    }
`;